import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Star, ChevronDown, User, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useEnquiry } from "@/context/EnquiryContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/courses", label: "Courses" },
  { to: "/results", label: "Results" },
  { to: "/gallery", label: "Gallery" },
  { to: "/videos", label: "Videos" },
  { to: "/contact", label: "Contact" },
  { to: "/pay-fees", label: "Pay Fees" },
];

export default function Header() {
  const { openEnquiry } = useEnquiry();
  const [mobileOpen, setMobileOpen] = useState(false);
  useLocation();

  const linkClass = ({ isActive }) =>
    `px-2 md:px-2.5 lg:px-3 py-2 text-[13px] lg:text-sm font-semibold rounded-md transition-colors whitespace-nowrap ${
      isActive ? "text-blue-900 bg-sky-50" : "text-slate-600 hover:text-blue-900 hover:bg-slate-50"
    }`;

  return (
    <>
      <div className="bg-slate-900 text-white py-2 px-4 text-xs sm:text-sm text-center" data-testid="announcement-bar">
        <span className="font-medium">Admissions Open 2026–27</span>
        <span className="mx-2 text-sky-400">•</span>
        <span className="text-slate-300">Limited seats per batch — small-batch personal attention</span>
      </div>

      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-slate-200/80" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 sm:h-20 items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
              <img src="/ptg-mark.png" alt="Prasad Tutor Group logo" className="h-11 w-11 rounded-xl object-cover shadow-md group-hover:scale-105 transition-transform" data-testid="logo-mark" />
              <div className="leading-tight">
                <div className="font-heading text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight">PTG</div>
                <div className="text-[10px] sm:text-[11px] font-semibold text-sky-700 uppercase tracking-widest">Est. 2003</div>
              </div>
            </Link>

            <nav className="hidden sm:flex items-center gap-0 md:gap-0.5 lg:gap-1 flex-nowrap" data-testid="desktop-nav">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkClass} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
                  {n.label}
                </NavLink>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="px-2 md:px-2.5 lg:px-3 py-2 text-[13px] lg:text-sm font-semibold rounded-md transition-colors whitespace-nowrap text-slate-600 hover:text-blue-900 hover:bg-slate-50 inline-flex items-center gap-1 outline-none" data-testid="nav-star-system">
                    <Star className="h-4 w-4 text-amber-500" /> Star System <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild><Link to="/student/login" data-testid="nav-student-login"><User className="h-4 w-4 mr-2" /> Student Login</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/teacher/login" data-testid="nav-teacher-login"><Users className="h-4 w-4 mr-2" /> Teacher Login</Link></DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            <div className="flex items-center gap-2">
              <Button onClick={() => openEnquiry()} className="hidden lg:inline-flex bg-amber-500 hover:bg-amber-600 text-white font-bold shadow-md" data-testid="header-enquiry-btn">
                Admission Enquiry
              </Button>

              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="sm:hidden" data-testid="mobile-menu-btn">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[280px]" data-testid="mobile-nav">
                  <div className="flex items-center gap-2 mb-6 mt-2">
                    <img src="/ptg-mark.png" alt="Prasad Tutor Group logo" className="h-9 w-9 rounded-lg object-cover" data-testid="mobile-logo-mark" />
                    <span className="font-heading font-extrabold text-slate-900">PTG</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {NAV.map((n) => (
                      <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setMobileOpen(false)}
                        className={({ isActive }) => `px-3 py-3 rounded-lg text-base font-semibold ${isActive ? "bg-sky-50 text-blue-900" : "text-slate-700"}`}
                        data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
                        {n.label}
                      </NavLink>
                    ))}
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <p className="px-3 py-1 text-xs font-bold uppercase tracking-widest text-amber-600 flex items-center gap-1.5"><Star className="h-3.5 w-3.5" /> Star System</p>
                      <NavLink to="/student/login" onClick={() => setMobileOpen(false)} className="px-3 py-3 rounded-lg text-base font-semibold text-slate-700 flex items-center gap-2" data-testid="mobile-nav-student-login"><User className="h-4 w-4" /> Student Login</NavLink>
                      <NavLink to="/teacher/login" onClick={() => setMobileOpen(false)} className="px-3 py-3 rounded-lg text-base font-semibold text-slate-700 flex items-center gap-2" data-testid="mobile-nav-teacher-login"><Users className="h-4 w-4" /> Teacher Login</NavLink>
                    </div>
                  </div>
                  <Button onClick={() => { setMobileOpen(false); openEnquiry(); }} className="w-full mt-6 bg-amber-500 hover:bg-amber-600 font-bold" data-testid="mobile-enquiry-btn">
                    Admission Enquiry
                  </Button>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
